import React, { useEffect, useState, useCallback } from "react";
import * as func from "../../api/apiHelper";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faScaleBalanced,
  faSpinner,
  faSearch,
  faGift,
  faBan,
  faExclamationTriangle,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

const PACK_TYPES = ["free", "single_unlock", "spark_bundle", "premium", "elite", "annual"];
const fmtDate = (d) => d ? new Date(d).toLocaleString("en-IN") : "—";

// Boost-credit tiers, mirroring APPLE_PRODUCT_TO_CREDITS in the backend's
// appleProductMap.js. Shown as suggestions, not a closed list — the operator can
// type any amount, because what is owed is whatever the customer actually paid for.
// apiHelper does NOT reject on a non-2xx. Its interceptor rejects with a plain
// STRING and rethrowIfCanceled catches that and RETURNS it, so `await func.post`
// resolves with a string (or an axios error) instead of throwing. A catch block
// therefore never runs, and `res.data.message` is undefined — which is how the
// server's "Already granted manually on ..." became a bare "Grant failed".
//
// This reads the message out of every shape apiHelper can hand back.
const serverMessage = (res, fallback) => {
  if (typeof res === "string" && res.trim()) return res;
  return res?.response?.data?.message || res?.data?.message || res?.message || fallback;
};

const CREDIT_TIERS = [100, 330, 680, 1200, 2600];

const GrantModal = ({ order, onSave, onClose, appleMode = false }) => {
  // Two things people buy, so two things this screen has to be able to deliver.
  // Credits were unreachable here until now, which meant a customer who paid for
  // boost credits and got nothing could not be made whole at all.
  const [kind, setKind] = useState("pack");
  // Did the money actually arrive? Deliberately unset — the operator must choose.
  // It is not paperwork: marking an order paid is what makes the earnings ledger
  // accrue matchmaker commission, so a wrong guess here pays real money out
  // against revenue that never existed.
  // In appleMode the question is already answered: a queue row only exists
  // because Apple charged the customer, so the money definitely arrived.
  const [basis, setBasis] = useState(appleMode ? "payment_received" : "");
  const [form, setForm] = useState({ packType: "premium", durationDays: 90, note: "Manual grant after payment verification" });
  const [credits, setCredits] = useState(100);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    // Send ONLY the fields for the chosen kind — the server picks its branch on
    // the presence of `credits`, so leaking a stray packType would grant a pack.
    await onSave(kind === "credits"
      ? { credits: Number(credits), note: form.note, basis }
      : { packType: form.packType, durationDays: Number(form.durationDays), note: form.note, basis });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center p-5 border-b">
          <h2 className="font-semibold text-lg text-gray-800">Deliver what was paid for</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><FontAwesomeIcon icon={faTimes} /></button>
        </div>
        <div className="p-5 space-y-4">
          {order && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
              <p>Order: <span className="font-mono font-medium">{order.readableTransactionId || order._id}</span></p>
              {/* An Apple stranding has no order and therefore no amount of
                  ours to show — Apple set and took the price. Saying so beats
                  rendering "₹undefined" or a fake zero. */}
              <p>Amount: {order.amount == null
                ? (appleMode ? "set and charged by Apple" : "unknown")
                : `₹${order.amount}`}</p>
              <p>Created: {fmtDate(order.createdAt)}</p>
            </div>
          )}
          {/* What did they actually buy? A pack and boost credits are different
              grants and the purchase itself usually says which. */}
          <div className="flex gap-2">
            {[["pack", "A pack"], ["credits", "Boost credits"]].map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setKind(k)}
                className={`flex-1 px-3 py-2 text-sm rounded-lg border transition ${
                  kind === k
                    ? "bg-indigo-600 text-white border-indigo-600 font-semibold"
                    : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {kind === "pack" ? (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Pack Type to Grant</label>
                <select
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  value={form.packType}
                  onChange={(e) => setForm(f => ({ ...f, packType: e.target.value }))}
                >
                  {PACK_TYPES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Duration (days)</label>
                <input
                  type="number"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  value={form.durationDays}
                  onChange={(e) => setForm(f => ({ ...f, durationDays: Number(e.target.value) }))}
                />
              </div>
            </>
          ) : (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Boost credits to add</label>
              <input
                type="number"
                min={1}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={credits}
                onChange={(e) => setCredits(e.target.value)}
              />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {CREDIT_TIERS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCredits(c)}
                    className={`px-2.5 py-1 text-xs rounded-full border transition ${
                      Number(credits) === c
                        ? "bg-indigo-50 border-indigo-300 text-indigo-700 font-semibold"
                        : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1.5">
                Match what they paid for. The ₹99 tier is 100 credits.
              </p>
            </div>
          )}
          {/* The money question. Asked before the note, because it changes what
              the platform owes, not just what the record says. Skipped for a
              stranded Apple purchase — Apple charging them is why the row exists. */}
          <div className={appleMode ? "hidden" : ""}>
            <label className="block text-xs font-medium text-gray-500 mb-1">Did the money reach us?</label>
            <div className="space-y-1.5">
              {[
                ["payment_received", "Yes — the gateway took it", "Settles the order. Matchmaker commission accrues, correctly."],
                ["goodwill", "No — this is goodwill", "Delivers the entitlement without recording revenue. No commission."],
              ].map(([v, label, hint]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setBasis(v)}
                  className={`w-full text-left px-3 py-2 rounded-lg border transition ${
                    basis === v
                      ? "border-indigo-400 bg-indigo-50"
                      : "border-gray-200 bg-white hover:bg-gray-50"
                  }`}
                >
                  <span className={`block text-sm ${basis === v ? "font-semibold text-indigo-800" : "text-gray-700"}`}>{label}</span>
                  <span className="block text-xs text-gray-500 mt-0.5">{hint}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Admin Note</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              value={form.note}
              onChange={(e) => setForm(f => ({ ...f, note: e.target.value }))}
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 p-5 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving || !basis}
            className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-60 flex items-center gap-2"
          >
            {saving && <FontAwesomeIcon icon={faSpinner} spin />}
            {!basis
              ? "Choose above first"
              : kind === "credits" ? `Grant ${credits || 0} credits` : `Grant ${form.packType}`}
          </button>
        </div>
      </div>
    </div>
  );
};

const PaymentReconciliation = () => {
  const [loading, setLoading] = useState(true);
  const [orphans, setOrphans] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [mode, setMode] = useState("orphans"); // 'orphans' | 'search'
  const [grantModal, setGrantModal] = useState(null); // { orderId, order }
  const [acting, setActing] = useState(null);
  const [loadError, setLoadError] = useState(null);
  // Apple purchases that failed verification. They are NOT PaymentOrders — the
  // verifier writes an order only on success — so without this the screen could
  // not see the very case it exists for.
  const [appleStranded, setAppleStranded] = useState([]);
  const [appleModal, setAppleModal] = useState(null);

  const fetchOrphans = useCallback(async () => {
    setLoading(true);
    try {
      const res = await func.get("mst-admin/reconciliation/orphans");
      if (res?.status === 200 && res.data?.success) {
        setOrphans(res.data.orphans || []);
        setAppleStranded(res.data.appleStranded || []);
        setLoadError(null);
      } else {
        // Without this branch a failing endpoint left orphans at [] and the
        // screen rendered "No orphaned payments. All clear!" — the most
        // dangerous possible empty state, since it says the opposite of what
        // happened. For the entire life of this screen the endpoint returned
        // 500, so "All clear!" is exactly what an operator always saw.
        setLoadError(serverMessage(res, "Could not load orphaned payments."));
        setOrphans([]);
      }
    } catch (err) {
      setLoadError(serverMessage(err, "Could not load orphaned payments."));
      setOrphans([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchOrphans();
  }, [fetchOrphans]);

  const handleSearch = async () => {
    if (!searchQ.trim()) return;
    setSearching(true);
    try {
      const res = await func.get(`mst-admin/reconciliation/search?q=${encodeURIComponent(searchQ.trim())}`);
      if (res?.status === 200 && res.data?.success) {
        setSearchResults(res.data.orders || []);
        setMode("search");
      } else {
        toast.error(serverMessage(res, "Search failed"));
      }
    } catch (err) {
      toast.error(serverMessage(err, "Search failed"));
    }
    setSearching(false);
  };

  const handleGrant = async (form) => {
    try {
      const res = await func.post(`mst-admin/reconciliation/${grantModal.orderId}/grant`, form);
      if (res?.status === 200 && res.data?.success) {
        toast.success(res.data.message);
        setGrantModal(null);
        if (mode === "orphans") fetchOrphans(); else if (searchQ.trim()) handleSearch();
      } else {
        toast.error(serverMessage(res, "Grant failed"));
      }
    } catch (err) {
      toast.error(serverMessage(err, "Grant failed"));
    }
  };

  const handleDeliverApple = async (form) => {
    try {
      const res = await func.post(
        `mst-admin/reconciliation/apple/${appleModal.row._id}/grant`,
        // No basis: Apple already took the money by the time a queue row exists,
        // so there is no goodwill case to distinguish here.
        form.credits ? { credits: form.credits, note: form.note }
                     : { packType: form.packType, durationDays: form.durationDays, note: form.note }
      );
      if (res?.status === 200 && res.data?.success) {
        toast.success(res.data.message);
        setAppleModal(null);
        fetchOrphans();
      } else {
        toast.error(serverMessage(res, "Delivery failed"));
      }
    } catch (err) {
      toast.error(serverMessage(err, "Delivery failed"));
    }
  };

  const handleMarkFailed = async (orderId) => {
    if (!window.confirm("Mark this payment as FAILED? This cannot be undone.")) return;
    setActing(orderId);
    try {
      const res = await func.post(`mst-admin/reconciliation/${orderId}/fail`, {});
      if (res?.status === 200 && res.data?.success) {
        toast.success("Payment marked as failed");
        if (mode === "orphans") fetchOrphans(); else if (searchQ.trim()) handleSearch();
        else setSearchResults(r => r?.filter(o => o._id !== orderId));
      } else {
        toast.error(serverMessage(res, "Action failed"));
      }
    } catch (err) {
      toast.error(serverMessage(err, "Action failed"));
    }
    setActing(null);
  };

  const displayOrders = mode === "orphans" ? orphans : (searchResults || []);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <FontAwesomeIcon icon={faScaleBalanced} className="text-2xl text-amber-600" />
        <h1 className="text-2xl font-bold text-gray-800">Payment Reconciliation</h1>
      </div>

      {/* Search bar */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
        <div className="flex-1 flex items-center border border-gray-200 rounded-lg px-3 py-2 gap-2">
          <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
          <input
            className="flex-1 outline-none text-sm"
            placeholder="Search by transaction ID, order ID..."
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
        </div>
        <button
          onClick={handleSearch}
          disabled={searching || !searchQ.trim()}
          className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
        >
          {searching && <FontAwesomeIcon icon={faSpinner} spin />}
          Search
        </button>
        {mode === "search" && (
          <button
            onClick={() => { setMode("orphans"); setSearchResults(null); setSearchQ(""); }}
            className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50"
          >
            Show Orphans
          </button>
        )}
      </div>

      {/* Orphan count banner */}
      {mode === "orphans" && orphans.length > 0 && (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-amber-600 text-xl" />
          <div>
            <p className="font-semibold text-amber-800">{orphans.length} orphaned payment{orphans.length > 1 ? "s" : ""} detected</p>
            <p className="text-sm text-amber-700">These payments have been PENDING for 30+ minutes. Investigate and either grant the pack or mark as failed.</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="p-4 border-b">
          <p className="font-semibold text-gray-700">
            {mode === "orphans" ? `Orphaned Payments (${orphans.length})` : `Search Results (${displayOrders.length})`}
          </p>
        </div>

        {/* Money Apple took that we never delivered. Deliberately ABOVE the
            orphan table: these are the ones where a customer has definitely
            been charged, so they are the more urgent list. */}
        {appleStranded.length > 0 && (
          <div className="mb-5 border border-red-200 rounded-xl overflow-hidden">
            <div className="bg-red-50 px-4 py-3 border-b border-red-200">
              <p className="text-sm font-semibold text-red-800">
                {appleStranded.length} Apple purchase{appleStranded.length === 1 ? "" : "s"} charged but never delivered
              </p>
              <p className="text-xs text-red-600 mt-0.5">
                Verification failed, so no payment order exists. Apple has the money; the customer has nothing.
              </p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 uppercase border-b text-left bg-white">
                  <th className="px-4 py-2">Transaction</th>
                  <th className="px-4 py-2">Product</th>
                  <th className="px-4 py-2">User</th>
                  <th className="px-4 py-2">Why it failed</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {appleStranded.map((a) => (
                  <tr key={a._id} className="border-b border-gray-50">
                    <td className="px-4 py-3 font-mono text-xs">{a.transactionId}</td>
                    <td className="px-4 py-3 text-xs">
                      {a.productId
                        ? <span className="font-mono">{a.productId.split(".").pop()}</span>
                        : <span className="text-gray-400">unknown</span>}
                      {/* Decoded from the receipt but NOT verified — verification
                          failing is why this row exists. Say so. */}
                      {a.productId && <span className="ml-1 text-[10px] text-gray-400">unverified</span>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{a.userId?.toString().slice(-8)}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 max-w-xs truncate" title={a.lastError || ""}>
                      {a.lastError || "—"}
                      <span className="block text-[10px] text-gray-400">{a.attempts} attempts · {a.status}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setAppleModal({ row: a })}
                        className="text-xs text-green-700 bg-green-50 hover:bg-green-100 px-2.5 py-1.5 rounded-lg font-medium"
                      >
                        Deliver
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-gray-400" />
          </div>
        ) : loadError && mode === "orphans" ? (
          /* Never say "All clear!" when we simply could not look. */
          <div className="text-center py-10">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-2xl text-red-400 mb-2" />
            <p className="text-sm text-red-600 font-medium">{loadError}</p>
            <p className="text-xs text-gray-400 mt-1">
              This is not "no stuck payments" — it is "we could not check".
            </p>
          </div>
        ) : displayOrders.length === 0 ? (
          <p className="text-center text-gray-400 py-10">
            {mode !== "orphans"
              ? "No results found."
              /* Never print "All clear!" above a list of undelivered purchases.
                 The orphan table and the Apple strandings are two different
                 sources of stuck money; only one of them being empty is not
                 all-clear. */
              : appleStranded.length > 0
                ? "No stuck payment orders — but see the undelivered Apple purchases above."
                : "No orphaned payments. All clear!"}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 uppercase border-b text-left">
                  <th className="px-4 py-3">Transaction ID</th>
                  <th className="px-4 py-3">User ID</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayOrders.map((o) => (
                  <tr key={o._id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs">{o.readableTransactionId || o.merchant_transaction_id || o._id}</td>
                    <td className="px-4 py-3 font-mono text-xs">{o.userId?.toString().slice(-8)}</td>
                    <td className="px-4 py-3 font-medium">
                      ₹{o.amount ?? "—"}
                      {o.pg_method === "APPLE_IAP" && (
                        <span className="ml-1.5 px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px] align-middle">Apple</span>
                      )}
                      {o.apple_product_id && (
                        <p className="text-[10px] text-gray-400 font-mono">{o.apple_product_id.split(".").pop()}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {/* payment_status, not status. Reading `o.status` rendered an
                          empty chip AND — because the action buttons were gated on
                          it — meant Grant and Mark Failed never appeared at all.
                          The value it compared, "PENDING", is not in PAYMENTSTATUS
                          either; the pending state is "INITIATED". */}
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        o.payment_status === "SUCCESS" ? "bg-green-100 text-green-700" :
                        o.payment_status === "FAILED" ? "bg-red-100 text-red-500" :
                        "bg-yellow-100 text-yellow-700"
                      }`}>
                        {o.payment_status ?? "—"}
                      </span>
                      {o.manuallyGranted && (
                        <p className="text-[10px] text-gray-400 mt-0.5">granted by hand</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs">{fmtDate(o.createdAt)}</td>
                    <td className="px-4 py-3">
                      {/* Actionable = not already delivered, and not already fixed
                          by hand. The server enforces both again (409); this only
                          keeps the operator from clicking into a refusal. */}
                      {o.payment_status !== "SUCCESS" && !o.manuallyGranted && (
                        <div className="flex gap-3">
                          <button
                            onClick={() => setGrantModal({ orderId: o._id, order: o })}
                            className="text-xs text-green-600 hover:text-green-800 flex items-center gap-1"
                          >
                            <FontAwesomeIcon icon={faGift} />
                            Deliver
                          </button>
                          <button
                            onClick={() => handleMarkFailed(o._id)}
                            disabled={acting === o._id}
                            className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 disabled:opacity-50"
                          >
                            <FontAwesomeIcon icon={acting === o._id ? faSpinner : faBan} spin={acting === o._id} />
                            Mark Failed
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {grantModal && (
        <GrantModal order={grantModal.order} onSave={handleGrant} onClose={() => setGrantModal(null)} />
      )}
      {appleModal && (
        <GrantModal
          order={{ readableTransactionId: appleModal.row.transactionId, amount: null,
                   createdAt: appleModal.row.createdAt }}
          appleMode
          onSave={handleDeliverApple}
          onClose={() => setAppleModal(null)}
        />
      )}
    </div>
  );
};

export default PaymentReconciliation;
